export type ContactModalField =
  | { name: string; label: string; type: "text" | "email"; required?: boolean; autoComplete?: string }
  | { name: string; label: string; type: "textarea"; required?: boolean; placeholder?: string; rows?: number }
  /** Renders the calendar picker. The form handler only accepts MM/DD/YYYY here. */
  | { name: string; label: string; type: "date"; required?: boolean };

export type ContactModalLogo = {
  src: string;
  alt: string;
  /**
   * Name of a per-mark size correction in `ContactRequestModal.module.css`
   * (the artwork has inconsistent padding). Renaming one there means renaming
   * it here too.
   */
  modifier?: string;
  srcSet?: string;
  sizes?: string;
};

export type ContactModalConfig = {
  id: "pentest" | "analysis" | "incident";
  title: string;
  description: string;
  formAction: string;
  submitLabel: string;
  successMessage: string;
  fields: ContactModalField[];
  logos?: readonly ContactModalLogo[];
};

export const PENTEST_FORM_ACTION = "https://forms.quantstamp.com/submit/infra-request";
export const ANALYSIS_FORM_ACTION = "https://forms.quantstamp.com/submit/flashsyn-request";
/*
  Backend dependency: this route must exist on the form handler (qs-form-handler),
  alongside infra-request / flashsyn-request, for incident submissions to land.
  Until it does, the modal renders and validates but submissions return an error.
*/
export const INCIDENT_FORM_ACTION = "https://forms.quantstamp.com/submit/incident-request";

export const pentestClientLogos: readonly ContactModalLogo[] = [
  { src: "/images/republic.svg", alt: "Republic", modifier: "republic" },
  {
    src: "/images/liquid.png",
    alt: "Liquid Collective",
    modifier: "collective",
    sizes: "(max-width: 504px) 100vw, 504px, 100vw",
    srcSet: "/images/liquid-p-500.png 500w, /images/liquid.png 504w",
  },
  { src: "/images/galxe2x.svg", alt: "GALXE" },
  { src: "/images/kiln.svg", alt: "Kiln", modifier: "kiln" },
  { src: "/images/luganodes.svg", alt: "Luganodes", modifier: "luganodes" },
  { src: "/images/twinstake.svg", alt: "Twinstake" },
  { src: "/images/rockx.svg", alt: "RockX", modifier: "rockx" },
  {
    src: "/images/contribution.png",
    alt: "Contribution DAO",
    modifier: "contribution",
    sizes: "(max-width: 2834px) 100vw, 2834px, 100vw",
    srcSet:
      "/images/contribution-p-500.png 500w, /images/contribution-p-800.png 800w, /images/contribution-p-1080.png 1080w, /images/contribution-p-1600.png 1600w, /images/contribution-p-2000.png 2000w, /images/contribution-p-2600.png 2600w, /images/contribution.png 2834w",
  },
];

export const pentestModalConfig: ContactModalConfig = {
  id: "pentest",
  title: "Request a Pentest",
  description: "Ready to protect your infra against configuration errors and external attacks?",
  formAction: PENTEST_FORM_ACTION,
  submitLabel: "Submit",
  successMessage:
    "Thank you for expressing your interest in an infra audit. You will receive a confirmation email from us and our team will be in touch with you within the next few days.",
  logos: pentestClientLogos,
  fields: [
    { name: "full_name", label: "Full Name", type: "text", required: true, autoComplete: "name" },
    { name: "telegram", label: "Telegram", type: "text", required: true, autoComplete: "off" },
    { name: "email", label: "Email", type: "email", required: true, autoComplete: "email" },
    { name: "project_name", label: "Project Name or Website URL", type: "text", required: true },
    { name: "timeline_deadline", label: "Timeline", type: "date", required: true },
    { name: "project_description", label: "Additional details", type: "textarea", rows: 5 },
  ],
};

export const analysisModalConfig: ContactModalConfig = {
  id: "analysis",
  title: "Request Analysis",
  description:
    "Quantstamp’s Economic Exploit Analysis service is accessible to both deployed and un-deployed protocols across all EVM chains.\n\nShare your details and someone from our team will reach out as soon as possible.",
  formAction: ANALYSIS_FORM_ACTION,
  submitLabel: "Send Request",
  successMessage:
    "Thank you for expressing your interest in an economic exploit analysis. You will receive a confirmation email from us and our team will be in touch with you within the next few days.",
  fields: [
    { name: "full_name", label: "Full Name", type: "text", required: true, autoComplete: "name" },
    { name: "email", label: "Email", type: "email", required: true, autoComplete: "email" },
    { name: "project_name", label: "Project Name or Website URL", type: "text", required: true },
    {
      name: "project_description",
      label: "Additional details",
      type: "textarea",
      placeholder: "Additional details...",
      rows: 5,
    },
  ],
};

export const incidentModalConfig: ContactModalConfig = {
  id: "incident",
  title: "Report a Security Incident",
  description:
    "Experiencing an active incident? Share the details and Quantstamp's response team will reach out as soon as possible.\n\nIf systems or funds are actively being compromised, include the fastest way to reach you.",
  formAction: INCIDENT_FORM_ACTION,
  submitLabel: "Contact the response team",
  successMessage:
    "We received your report. Quantstamp's incident-response team will reach out as soon as possible. For a time-critical, active incident you can also email security@quantstamp.com directly.",
  fields: [
    { name: "full_name", label: "Full Name", type: "text", required: true, autoComplete: "name" },
    { name: "email", label: "Email", type: "email", required: true, autoComplete: "email" },
    { name: "contact", label: "Fastest way to reach you (Telegram, phone, Signal)", type: "text", autoComplete: "off" },
    { name: "project_name", label: "Project Name or Website URL", type: "text", required: true },
    {
      name: "project_description",
      label: "What's happening?",
      type: "textarea",
      placeholder: "Describe the incident: what you're seeing, when it started, and which systems or funds are affected...",
      rows: 5,
      required: true,
    },
  ],
};

export const contactModalConfigs = {
  pentest: pentestModalConfig,
  analysis: analysisModalConfig,
  incident: incidentModalConfig,
} as const;

export type ContactModalId = keyof typeof contactModalConfigs;
